interface ErrorMessages {
    [key: string]: string;
}

class SignInValidation {
    private form: HTMLFormElement | null;
    private requiredInputs: NodeListOf<HTMLInputElement>;
    private phoneInput: HTMLInputElement | null;
    private errorMessages: ErrorMessages;

    constructor() {
        // Cible le formulaire d'inscription par son id, et non par `.sign-in__form` :
        // ce script est charge sur TOUTES les pages par _Layout, et il detourne la
        // soumission (preventDefault + fetch + redirection vers "/"). Un autre
        // formulaire reprenant la classe pour son style — l'ecran d'acceptation des
        // CGU, par exemple — se verrait alors vole sa soumission, sans erreur visible.
        this.form = document.querySelector('#formPro');
        this.requiredInputs = this.form?.querySelectorAll('input[required]') ?? document.querySelectorAll('input[required]');
        this.phoneInput = document.querySelector('input[name="Phone"]');
        this.errorMessages = {
            Company: 'Le nom de la société est requis',
            Siren: 'Le SIREN est requis (9 chiffres)',
            LastName: 'Le nom est requis',
            FirstName: 'Le prénom est requis',
            Address: 'L\'adresse est requise',
            ZipCode: 'Le code postal est requis',
            City: 'La ville est requise',
            Phone: 'Le numéro de téléphone est requis',
            Email: 'L\'email est requis',
            Password: 'Le mot de passe est requis',
            AcceptCgu: 'Vous devez accepter les Conditions Générales d\'Utilisation pour créer un compte'
        };
        this.initialize();
    }

    private initialize(): void {
        // Hors page d'inscription, il n'y a rien a cabler : ne pas retomber sur tous
        // les `input[required]` du document, on poserait des messages d'erreur sur des
        // formulaires qui ont leur propre validation.
        if (!this.form) return;

        if (this.phoneInput) {
            this.phoneInput.addEventListener('input', this.handlePhoneInput.bind(this));
            if (!this.phoneInput.value) {
                this.phoneInput.value = '+33 ';
            }
        }

        this.requiredInputs.forEach(input => {
            // Une case a cocher n'emet un `blur` exploitable qu'apres coup : on ecoute
            // `change` pour que le message disparaisse des qu'elle est cochee.
            input.addEventListener(input.type === 'checkbox' ? 'change' : 'blur',
                () => this.validateInput(input));
        });

        if (this.form) {
            this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
    }

    private handleSubmit(event: Event): void {
        event.preventDefault();
        let isValid = true;

        this.requiredInputs.forEach(input => {
            if (!this.validateInput(input)) {
                isValid = false;
            }
        });

        if (isValid && this.form) {
            const formData = new FormData(this.form as HTMLFormElement);
            fetch(this.form.action, {
                method: 'POST',
                body: formData
            }).then(response => {
                if (response.ok) {
                    // @ts-ignore
                    kendo.alert("Votre compte a bien été créé");
                    window.location.href = '/';
                } else {
                    // @ts-ignore
                    kendo.alert("Une erreur s'est produite");
                }
            });
        }
    }

    private handlePhoneInput(e: Event): void {
        const input = e.target as HTMLInputElement;
        let value = input.value.replace(/[^0-9+\s]/g, '');
        let cursorPosition = input.selectionStart ?? 0;

        if (!value.startsWith('+33')) {
            value = '+33' + value.replace(/\+33/g, '');
        }

        let digits = value.substring(value.indexOf('+33') + 3).replace(/[^0-9]/g, '');
        if (digits.length > 9) digits = digits.slice(0, 9);

        value = '+33';
        if (digits.length > 0) {
            const firstDigit = digits[0];
            value += ' ' + (firstDigit === '6' || firstDigit === '7' ? firstDigit : '6');

            if (digits.length > 1) value += ' ' + digits.slice(1, 3);
            if (digits.length > 3) value += ' ' + digits.slice(3, 5);
            if (digits.length > 5) value += ' ' + digits.slice(5, 7);
            if (digits.length > 7) value += ' ' + digits.slice(7, 9);
        } else {
            value += ' ';
        }

        if (cursorPosition <= 3) {
            cursorPosition = 4;
        } else {
            const beforeCursor = value.slice(0, cursorPosition);
            const spacesBeforeCursor = beforeCursor.split(' ').length - 1;
            cursorPosition = Math.min(value.length, cursorPosition + spacesBeforeCursor);
        }

        input.value = value;
        input.setSelectionRange(cursorPosition, cursorPosition);
    }

    private validateInput(input: HTMLInputElement): boolean {
        const errorSpan = document.querySelector(`[data-valmsg-for="${input.getAttribute('name')}"]`);
        if (!errorSpan) return true;

        const sameBillingAddress = document.querySelector('input[name="SameBillingAddress"]') as HTMLInputElement;
        if (sameBillingAddress?.checked && ['BillingAddress', 'BillingZipCode', 'BillingCity'].includes(input.getAttribute('name') ?? '')) {
            errorSpan.textContent = '';
            return true;
        }

        // Une case a cocher a toujours une `value` ("on" par defaut) : le controle de
        // chaine vide ci-dessous la laisserait passer decochee. On teste `checked`.
        if (input.type === 'checkbox') {
            if (input.required && !input.checked) {
                this.showError(errorSpan, this.errorMessages[input.getAttribute('name') ?? ''] || 'Ce champ est requis');
                return false;
            }

            errorSpan.textContent = '';
            return true;
        }

        if (!input.value.trim()) {
            this.showError(errorSpan, this.errorMessages[input.getAttribute('name') ?? ''] || 'Ce champ est requis');
            return false;
        }

        if (input.type === 'email' && input.value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(input.value)) {
                this.showError(errorSpan, 'Veuillez entrer une adresse email valide');
                return false;
            }
        }

        if (input.type === 'password' && input.value) {
            const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9]).{8,}$/;
            if (!passwordRegex.test(input.value)) {
                this.showError(errorSpan, 'Le mot de passe doit contenir au moins 8 caractères, une majuscule et un caractère spécial');
                return false;
            }
        }

        if (input.name === 'Phone' && input.value) {
            const phoneRegex = /^\+33\s[67](?:\s\d{2}){4}$/;
            if (!phoneRegex.test(input.value)) {
                this.showError(errorSpan, 'Le numéro de téléphone doit être au format +33 6 XX XX XX XX');
                return false;
            }
        }

        if (input.name === 'Siren' && input.value) {
            const sirenRegex = /^\d{9}$/;
            if (!sirenRegex.test(input.value)) {
                this.showError(errorSpan, 'Le SIREN doit contenir exactement 9 chiffres');
                return false;
            }
        }

        errorSpan.textContent = '';
        return true;
    }

    private showError(errorSpan: Element, message: string): void {
        errorSpan.textContent = message;
        errorSpan.setAttribute('style', 'color: #FF0000');
        errorSpan.classList.add('text-danger', 'Poppins-Regular-S');
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new SignInValidation();
});
